///
/// EarthBrowser - CSV File Handler
///
/// Handles CSV file drag & drop
/// Created by Matt Giger on December 17, 2025
/// Copyright (c) 2025 Geodata Labs
///

import { registerDatasetSourceFile } from '@/components/features/user-datasets/datasetSourceFiles'
import { DataProviderRegistry } from '@/data-providers/DataProviderRegistry'
import { MemoryDataProvider } from '@/data-providers/MemoryDataProvider'
import type { DataRow } from '@/data-providers/types'
import { getOrCreateDatasetWindow } from '@/utils/windowUtils'
import type { CSVParseDataChunk, CSVParseProgress, CSVWorkerType } from '@/workers/csv/index'
import CSVWorker from '@/workers/csv/index.ts?worker'
import { proxy, wrap, type Remote } from 'comlink'
import { offerPlacemarkFromPoints } from '../placemarkFromPoints'
import { BaseFileHandler } from './BaseFileHandler'

export class CSVFileHandler extends BaseFileHandler {
	private workerRef: Remote<CSVWorkerType> | null = null
	private worker: Worker | null = null

	canHandle(file: File): boolean {
		return file.name.toLowerCase().endsWith('.csv')
	}

	// No-op - worker is created on demand
	initialize(): void {}

	/**
	 * Ensures the worker is created, creating it on-demand if needed
	 */
	private ensureWorker(): void {
		if (!this.worker) {
			this.worker = new CSVWorker()
			this.workerRef = wrap<CSVWorkerType>(this.worker)
		}
	}

	cleanup(): void {
		if (this.worker) {
			this.worker.terminate()
			this.worker = null
			this.workerRef = null
		}
	}

	/** Returns the created window id (null when the file is already open) */
	async handleFile(file: File, options?: { openDrawer?: boolean }): Promise<string | null> {
		// Create worker on-demand
		this.ensureWorker()

		if (!this.workerRef) {
			console.error('CSV worker could not be created')
			return null
		}

		// Get or create window for this file
		const windowId = getOrCreateDatasetWindow(
			this.context.getState,
			this.context.dispatch,
			file.name,
			'csv',
			{ width: 700, height: 500, openDrawer: options?.openDrawer }
		)

		// If window already exists, do nothing
		if (!windowId) {
			return null
		}

		try {
			// Accumulate data chunks as they arrive
			const allData: Record<string, unknown>[] = []

			// Create MemoryDataProvider with empty data initially
			const provider = new MemoryDataProvider({
				id: windowId,
				name: file.name,
				data: [],
			})

			// Register the provider immediately - this makes it available to the window
			DataProviderRegistry.register(windowId, provider)

			// Parse CSV in worker with progress reporting and chunked data transfer
			await this.workerRef.parseFile(
				file,
				proxy((progress: CSVParseProgress) => {
					// Dispatch progress event for the window to listen to
					// We don't know total rows until parsing is complete, so show percentage based on bytes
					window.dispatchEvent(new CustomEvent(`dataset:progress:${windowId}`, {
						detail: {
							windowId,
							filename: file.name,
							processedRows: progress.rowsParsed,
							percentage: progress.percentage
						}
					}))
				}),
				proxy((dataChunk: CSVParseDataChunk) => {
					// Accumulate data as it arrives from worker
					allData.push(...dataChunk.chunk)
				})
			)

			// Update provider with all the parsed data
			// We need to replace the internal data since MemoryDataProvider copies it
			const finalProvider = new MemoryDataProvider({
				id: windowId,
				name: file.name,
				data: allData as DataRow[],
			})

			// Re-register with the complete data
			DataProviderRegistry.register(windowId, finalProvider)

			// Keep the original file around for cloud upload (before the
			// complete event so the browser toolbar sees it on first render)
			registerDatasetSourceFile(windowId, { file, format: 'csv', featureCount: allData.length })

			// Dispatch completion event for the window to listen to
			window.dispatchEvent(new CustomEvent(`dataset:complete:${windowId}`, {
				detail: {
					windowId,
					filename: file.name,
					rowCount: allData.length
				}
			}))

			console.log(`CSV file loaded: ${file.name} (${allData.length} rows)`)

			// Small clean point files can become a placemark instead
			offerPlacemarkFromPoints(this.context.dispatch, windowId, file.name, allData, 'csv')
			return windowId
		} catch (error) {
			console.error('Failed to load CSV file:', error)

			// Dispatch error event
			window.dispatchEvent(new CustomEvent(`dataset:error:${windowId}`, {
				detail: {
					windowId,
					filename: file.name,
					error: error instanceof Error ? error.message : String(error)
				}
			}))

			throw error
		}
	}
}

